import {Component, OnDestroy, OnInit, QueryList, ViewChildren} from '@angular/core';
import {TimeSyncService} from '../../services/timeSync.service';
import {UIChart} from 'primeng/chart';
import {GraphHandlerService} from '../bw-graph/graph-handler.service';
import {BaseClientComponent} from '../base-client.component';
import {AuthenticationService} from '../../authenticationModule/services/authentication.service';

@Component({
  selector: 'app-time-sync',
  templateUrl: './time-sync.component.html',
  styleUrls: ['./time-sync.component.scss']
})
export class TimeSyncComponent extends BaseClientComponent implements OnInit, OnDestroy {
  @ViewChildren('chart') charts !: QueryList<UIChart>;
  clientTimeSyncXcos = [];
  selectedXco = '';
  timeSyncData: any;
  data = [];
  selectedPeriod = [];
  selectedUnit = [];
  isZoomedIn = [];
  borderColorsArray = ['#3366CC', '#177430', '#b62c38'];

  graphsSectionsArray = new Map<string, any>([
    ['Client Service Accuracy Statistics', ['lockingStatus', 'neighborStatus', 'neighborLockingStatus', 'nbHops', ['utcOffset', 'offsetFromNeighbor', 'neighborUtcOffset']]],
    ['Client SFP Port Stats', ['temp', 'vcc', 'txBias', ['txPow', 'rxPow'], 'detect']],
    ['Client Link Statistics', [['txPackets', 'rxPackets'], 'linkStatus', ['txBytes', 'rxBytes'], ['txErrors', 'rxErrors']]],
    ['Client Service Precision Statistics', ['offsetFromMaster', 'clientGlobalStatus']]
  ]);

  graphOptions = {
    parsing: true,
    scales: {
      x: {
        stacked: true,
        type: 'time',
        ticks: {
          callback: function (value) {
            return value;
          },
        },
        min: this.graphHandlerService.minTime,
        time: {
          parser: 'YYYY-MM-DD HH:mm',
          unit: 'hour',
          displayFormats: {
            day: 'MM-DD HH',
            hour: 'HH:mm'
          }
        },
        scaleLabel: {
          display: true,
          stacked: true,
          labelString: 'Date'
        }
      }
    },
    plugins: {
      zoom: {
        pan: {
          enabled: true,
          mode: 'x'
        },
        zoom: {
          enabled: false,
          mode: 'x',
          speed: 0.1,
        }
      }
    },
  };
  currentRes: any;
  lastRefreshedDataAt = '';
  isDataObsolete = false;
  private timeoutId: any;

    constructor(
        private timeSyncService: TimeSyncService,
        private authenticationService: AuthenticationService,
        public graphHandlerService: GraphHandlerService) {
        super();
    }

  ngOnInit() {
    const response$ = this.authenticationService.waitForAuthenticationThenLaunch(() => {
      this.timeSyncService.getClientTimeSyncXcos().subscribe(
        res => {
          this.clientTimeSyncXcos = res;
          this.selectedXco = this.clientTimeSyncXcos[0];
          this.loadTimeSyncData();
        });
    });
    this.subscriptions.push(response$);
  }

  changeSelectedXco($event: any) {
    this.selectedXco = this.clientTimeSyncXcos[$event.index];
    this.loadTimeSyncData();
  }

  loadTimeSyncData() {
    this.timeSyncService.getTimeSyncDataByXco(this.selectedXco).subscribe(
      res => {
        this.timeSyncData = res;
        this.currentRes = res[res.length - 1];
        this.initGraph(['lockingStatus'], ['NIST Service Locking Status'], res);
        this.initGraph(['neighborLockingStatus'], ['NIST Service Locking Status To Neighbor'], res);
        this.initGraph(['neighborStatus'], ['NIST Service Neighbor Locking Status'], res);
        this.initGraph(['temp'], ['Temperature'], res);
        this.initGraph(['vcc'], ['Voltage'], res);
        this.initGraph(['txBias'], ['Transmit Bias Current'], res);
        this.initGraph(['detect'], ['Detection Status'], res);
        this.initGraph(['linkStatus'], ['Link Status'], res);
        this.initGraph(['nbHops'], ['Number of Hops'], res);
        this.initGraph(['offsetFromMaster'], ['Offset From Master'], res);
        this.initGraph(['clientGlobalStatus'], ['Client Global Status'], res);
        this.initGraph(['utcOffset', 'offsetFromNeighbor', 'neighborUtcOffset'], ['NIST Service UTC Offset', 'NIST Service Offset From neighbor', 'NIST Service Neighbor UTC Offset'], res);
        this.initGraph(['txPow', 'rxPow'], ['Transmit Power', 'Receive Power'], res);
        this.initGraph(['txBytes', 'rxBytes'], ['Transmitted Bytes', 'Received Bytes'], res);
        this.initGraph(['txErrors', 'rxErrors'], ['Transmit Errors', 'Received Errors'], res);
        this.initGraph(['txPackets', 'rxPackets'], ['Transmitted Packets', 'Received Packets'], res);
        const nowUtc = new Date().toISOString();
        this.lastRefreshedDataAt = nowUtc.substring(10, 16).replace('T', ' ') + ' UTC';
        this.isDataObsolete = false;
        clearTimeout(this.timeoutId);
        this.timeoutId = setTimeout(() => {
          this.isDataObsolete = true;
        }, 60000);
      }
    );
  }

  private initGraph(id, labels, res, type = 'line') {
    this.selectedPeriod[id] = 360;
    this.isZoomedIn[id] = false;
    this.selectedUnit[id] = 'hour';
    this.data[id] = {};
    this.data[id][this.selectedXco] = {};
    this.data[id][this.selectedXco].labels = res.map(data => data.date);
    this.data[id][this.selectedXco].datasets = this.getGraphDataset(labels, res, id, type);
  }

  getGraphDataset(labels: any, res, id: string, type: string) {
    const dataArray = [];
    for (let i = 0; i < labels.length; i++) {
      dataArray.push(
        {
          'stacked': true,
          'type': type,
          'label': labels[i],
          'data': res.map(data => data[id[i]]),
          'id': id,
          'xco': this.selectedXco,
          'borderColor': this.borderColorsArray[i],
          'backgroundColor': this.borderColorsArray[i],
          'lineTension': 0,
          'fill': false,
          'pointHoverRadius': 5,
          'pointRadius': 2,
          'pointHoverBackgroundColor': 2,
        }
      );
    }
    return dataArray;
  }

  resetCharZoom(xco: string, id: string) {
    this.charts.map((chart: UIChart) => {
      if (chart.data && chart.data.datasets[0].xco === xco && (chart.data.datasets[0].id.join() === id
        || (Array.isArray(id) && chart.data.datasets[0].id.join() === id.join()))) {
        chart.options.scales.x.max = null;
        chart.options.scales.x.min = this.graphHandlerService.minTime;
        chart.reinit();
      }
    });
  }

  updateTimeScale(xco: string, id: string) {
    this.charts.map((chart: UIChart) => {
      if (chart.data && chart.data.datasets[0].xco === xco && (chart.data.datasets[0].id.join() === id
        || (Array.isArray(id) && chart.data.datasets[0].id.join() === id.join()))) {
        const endDate = new Date();
        const maxTime = endDate.getTime();
        endDate.setMinutes(endDate.getMinutes() - this.selectedPeriod[id]);
        chart.options.scales.x.min = endDate.getTime();
        chart.options.scales.x.max = maxTime;
        chart.options['scales']['x']['time']['unit'] = this.selectedUnit[id];
        chart.reinit();
      }
    });
  }

  zoomIn(id: string) {
    this.isZoomedIn[id] = true;
  }

  zoomOut(id: string) {
    this.isZoomedIn[id] = false;
  }

  keepOriginalOrder() {
    return 0;
  }

  ngOnDestroy() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }
}
